// src/pages/programs/ShowProgram.jsx
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import GlobalBreadcrumbs from "../../components/global/GlobalBreadcrumbs";
import useGetProgram from "../../hooks/programs/useGetProgram";
import LoadingPage from "../../components/global/LoadingPage";
import WrapperSection from "../../components/global/WrapperSection";
import ProgramActions from "../../components/programs/ShowProgram/ProgramActions";
import ProgramHeader from "../../components/programs/ShowProgram/ProgramHeader";
import ProgramOverview from "../../components/programs/ShowProgram/ProgramOverview";
import ProgramDayCard from "../../components/programs/ShowProgram/ProgramDayCard";
import ProgramPricing from "../../components/programs/ShowProgram/ProgramPricing";

import "../../styles/PrintStyles.css";
import PrintProgram from "../../components/programs/ShowProgram/PrintProgram";
import useConfirmProgram from "../../hooks/programs/useConfirmProgram";

const ShowProgram = () => {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const { program, isLoading, error } = useGetProgram(id);
    const { confirmProgramMutate, isPending: isConfirming } = useConfirmProgram();

    const enrichedDays = useMemo(() => {
        if (!program?.days) return [];
        
        // 1. Build a complete consecutive array of days
        const allDaysMap = new Map();
        program.days.forEach(d => {
            allDaysMap.set(d.date, { ...d, products: [...(d.products || [])] });
        });

        // Fill missing days if start_date and durations are available
        if (program.start_date && program.durations) {
            const startDateObj = new Date(program.start_date);
            for (let i = 0; i < program.durations; i++) {
                const currentDate = new Date(startDateObj);
                currentDate.setUTCDate(currentDate.getUTCDate() + i);
                const dateStr = currentDate.toISOString().split('T')[0];
                
                if (!allDaysMap.has(dateStr)) {
                    allDaysMap.set(dateStr, {
                        date: dateStr,
                        day_name: { ar: '', en: '' },
                        products: [],
                    });
                }
            }
        }

        // Convert map to sorted array
        const days = Array.from(allDaysMap.values()).sort((a, b) => a.date.localeCompare(b.date));

        // 2. Expand original hotels
        days.forEach((currentDay) => {
            // ONLY pick original hotels (no _dayKey) to prevent cascading infinite loops
            const hotelProducts = currentDay.products.filter(p => p.hotel_details && !p._dayKey);
            
            hotelProducts.forEach((hotelProduct) => {
                const nights = parseInt(hotelProduct.nights) || 1;
                const checkInDate = new Date(currentDay.date);
                
                for (let i = 1; i < nights; i++) {
                    const targetDate = new Date(checkInDate);
                    targetDate.setUTCDate(targetDate.getUTCDate() + i); // Safely advance by i days ignoring timezones
                    const targetDateStr = targetDate.toISOString().split('T')[0];
                    
                    const targetDay = days.find(d => d.date === targetDateStr);
                    if (targetDay) {
                        if (!targetDay.products.some(p => p.hotel_details && p.id === hotelProduct.id)) {
                            targetDay.products.push({ ...hotelProduct, _dayKey: `${hotelProduct.id}_${targetDay.date}` });
                        }
                    }
                }
            });
        });

        return days;
    }, [program]);


    // Set document title for PDF export filename
    React.useEffect(() => {
        if (program?.title) {
            document.title = `${program.title[lng]}`;
        }
    }, [program, lng]);

    if (isLoading) {
        return <LoadingPage />;
    }

    if (error) {
        return (
            <Container maxWidth="xl">
                <Typography color="error" sx={{ mt: 4 }}>
                    {isArabic ? "حدث خطأ أثناء تحميل البرنامج" : "Error loading program"}
                </Typography>
            </Container>
        );
    }

    return (
        <WrapperSection>
            {/* ========== العناصر المخفية عند الطباعة ========== */}
            <div className="no-print">
                <GlobalBreadcrumbs />
            </div>

            <div className="no-print">
                <ProgramActions program={program} confirmProgramMutate={confirmProgramMutate} isConfirming={isConfirming} />
            </div>

            {/* ========== نسخة الطباعة - تظهر فقط عند Print ========== */}
            <div className="print-only">
                <PrintProgram program={program} lang={lng} />
            </div>

            {/* ========== المحتوى للعرض العادي والطباعة ========== */}
            <Box id="program-content">
                <ProgramHeader program={program} />
                <ProgramOverview program={program} confirmProgramMutate={confirmProgramMutate} isConfirming={isConfirming}/>

                <Box className="days-list" sx={{ 
                    mt: 4,
                    '@media print': {
                        pageBreakBefore: 'auto',
                        mt: 1
                    }
                }}>
                    {enrichedDays.map((day, index) => (
                        <ProgramDayCard
                            key={day.id}
                            day={day}
                            dayNumber={index + 1}
                            totalDays={enrichedDays.length}
                        />
                    ))}
                </Box>

                <ProgramPricing program={program} />
            </Box>
        </WrapperSection>
    );
};

export default ShowProgram;
